import { SCOPE_BOXES } from "@/api/access/access.types";
import type { RoleScopeKinds, ScopeBox, ScopeKind } from "@/api/access/access.types";

export type Reach = "own" | "team" | "everything";

export type ReachOption = {
  value: Reach;
  name: string;
  /** One sentence, used verbatim on the Reach card, the Edit Reach radios and anywhere else. */
  meaning: string;
};

/**
 * The three reaches, worded once. The roles list, the Reach card and the Edit Reach modal all read
 * them from here so the same reach never reads two ways (Jesus, 2026-10-23).
 */
export const REACH_OPTIONS: ReachOption[] = [
  { value: "own", name: "Own", meaning: "Work assigned to them or created by them." },
  { value: "team", name: "Team", meaning: "Work of the clients on their client team." },
  {
    value: "everything",
    name: "Everything in scope",
    meaning: "All work inside their entities and clients.",
  },
];

export const reachOption = (reach: Reach | null | undefined): ReachOption | null =>
  REACH_OPTIONS.find((option) => option.value === reach) ?? null;

/** The scope boxes the access engine knows, in the order the Scope tab lists them. */
export const SCOPE_BOX_LABEL: Record<ScopeBox, string> = {
  entities: "Entities",
  clients: "Clients",
  trade: "Trades",
  state: "Areas / territories",
  brand: "Brand",
  priority: "Priority",
  site: "Site",
  work_order_type: "Work order type",
  subtrade: "Subtrades",
};

const KIND_LABEL: Record<ScopeKind, string> = {
  required: "Required",
  optional: "Optional",
  not_used: "Not used",
};

/** The three ways a role can use a box, as Edit Scope and New Role offer them (SERV-3592). */
export const SCOPE_KIND_OPTIONS: { value: ScopeKind; label: string }[] = [
  { value: "required", label: KIND_LABEL.required },
  { value: "optional", label: KIND_LABEL.optional },
  { value: "not_used", label: KIND_LABEL.not_used },
];

/** Every box with how the role uses it; the scope editors hold all nine. */
export type AllScopeKinds = Record<ScopeBox, ScopeKind>;

const kindOf = (stored: ScopeKind | undefined): ScopeKind =>
  stored === "required" || stored === "optional" ? stored : "not_used";

/** A box the record leaves out counts as not used (contract 3.2, D245). */
export const allScopeKindsOf = (scopeKinds: RoleScopeKinds | null | undefined): AllScopeKinds =>
  Object.fromEntries(
    SCOPE_BOXES.map((box): [ScopeBox, ScopeKind] => [box, kindOf(scopeKinds?.[box])]),
  ) as AllScopeKinds;

/**
 * The kinds a save sends: the boxes the role uses, the rest left out, which the server reads as not
 * used. A role saved from an editor is stored in the shape the seed writes, so its History row names
 * only the boxes that changed.
 */
export const storedScopeKinds = (kinds: AllScopeKinds): RoleScopeKinds =>
  Object.fromEntries(
    SCOPE_BOXES.filter((box) => kinds[box] !== "not_used").map((box): [ScopeBox, ScopeKind] => [
      box,
      kinds[box],
    ]),
  ) as RoleScopeKinds;

/**
 * The boxes a save makes Required that were not, which leaves a holder with nothing in one of them
 * unable to see any work in that company (SERV-3592, D280). Entities is never one: a holder of a
 * company's copy is a member of that company, so it empties nothing for anyone holding the role.
 */
export const boxesGoingRequired = (
  from: RoleScopeKinds | null | undefined,
  to: AllScopeKinds,
): ScopeBox[] =>
  SCOPE_BOXES.filter(
    (box) => box !== "entities" && to[box] === "required" && kindOf(from?.[box]) !== "required",
  );

export type ScopeBoxRow = {
  box: ScopeBox;
  label: string;
  kind: ScopeKind;
  kindLabel: string;
};

/**
 * The boxes this role puts in play, as the role record stores them, for the lists that name only
 * what a holder fills in.
 */
export function buildScopeBoxRows(scopeKinds: RoleScopeKinds | null | undefined): ScopeBoxRow[] {
  return buildAllScopeBoxRows(scopeKinds).filter((row) => row.kind !== "not_used");
}

/**
 * Every box, for the role's Scope tab. A box the record leaves out counts as not used (contract
 * 3.2), so it reads "Not used", as Compare roles shows it, never a blank (D245).
 */
export function buildAllScopeBoxRows(scopeKinds: RoleScopeKinds | null | undefined): ScopeBoxRow[] {
  if (!scopeKinds) return [];

  return SCOPE_BOXES.map((box) => {
    const kind = kindOf(scopeKinds[box]);
    return { box, label: SCOPE_BOX_LABEL[box], kind, kindLabel: KIND_LABEL[kind] };
  });
}
